import React from 'react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import type { IModalDialogContent, ModalDialogOptions, ModalWindow } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';
import apiService from '@services/apiService.ts';
import { ImgCheck } from '@components/Images/ImgCheck.tsx';

const DURATION_OPTIONS = [
  { label: '5 minutes (default)', value: 5 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '8 hours', value: 480 },
  { label: '12 hours', value: 720 },
  { label: '24 hours', value: 1440 },
];

type Props = { fileId: string; fileName: string };

@observer
export class AccessCodeDialog extends React.Component<Props> implements IModalDialogContent<void> {
  @observable private _step: 'configuring' | 'generating' | 'generated' | 'error' = 'configuring';
  @observable private _durationMinutes = 5;
  @observable private _behavior: 'download' | 'page' = 'download';
  @observable private _code = '';
  @observable private _expiresAt = '';
  @observable private _error = '';
  @observable private _copySuccess = false;

  constructor(props: Props) {
    super(props);
    makeObservable(this);
  }

  getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    return {
      title: 'Share via Code',
      width: '380px',
      closeByEscape: true,
      closeOnClickOutside: true,
      buttons: [
        {
          title: 'Close',
          type: ModalButtonType.Cancel,
          onClick: () => window.close(ModalButtonType.Cancel),
        },
      ],
    };
  }

  render() {
    return this._step === 'generated' ? this._renderCode() : this._renderConfig();
  }

  private _renderConfig() {
    const isGenerating = this._step === 'generating';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>File</label>
          <div style={{ fontSize: '13px', opacity: 0.8, padding: '6px 8px', background: 'var(--input-bg, rgba(128,128,128,0.1))', borderRadius: '4px' }}>
            {this.props.fileName}
          </div>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>Code expires after</label>
          <select className="form-control" value={this._durationMinutes} onChange={this._onDurationChange} disabled={isGenerating}>
            {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>When code is entered</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="radio" name="code-behavior" value="download" checked={this._behavior === 'download'} onChange={this._onBehaviorChange} disabled={isGenerating} />
              Instant download
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="radio" name="code-behavior" value="page" checked={this._behavior === 'page'} onChange={this._onBehaviorChange} disabled={isGenerating} />
              Open download page
            </label>
          </div>
        </div>
        {this._error && (
          <div style={{ color: 'var(--danger-color, #ef4444)', fontSize: '13px' }}>{this._error}</div>
        )}
        <button
          className="btn btn-primary"
          onClick={this._generate}
          disabled={isGenerating}
          style={{ width: '100%' }}
        >
          {isGenerating ? 'Generating…' : 'Generate Code'}
        </button>
      </div>
    );
  }

  private _renderCode() {
    const expiresAt = this._expiresAt ? new Date(this._expiresAt) : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', textAlign: 'center' }}>
        <div style={{ fontSize: '13px', opacity: 0.8 }}>
          Share this code to give access to <b>{this.props.fileName}</b>
        </div>
        <div style={{
          fontFamily: 'monospace',
          fontSize: '40px',
          fontWeight: 700,
          letterSpacing: '10px',
          padding: '16px 28px',
          background: 'var(--input-bg, rgba(128,128,128,0.1))',
          borderRadius: '8px',
          userSelect: 'all',
        }}>
          {this._code}
        </div>
        <button className="btn btn-secondary" onClick={this._onCopy} style={{ alignSelf: 'stretch' }}>
          {this._copySuccess ? <><ImgCheck /> Copied!</> : 'Copy Code'}
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', opacity: 0.65 }}>
          {expiresAt && <span>Expires {expiresAt.toLocaleString()}</span>}
          <span>{this._behavior === 'download' ? 'Will start download immediately' : 'Will open download page'}</span>
        </div>
        <button className="btn btn-secondary" onClick={this._onGenerateNew} style={{ alignSelf: 'stretch', opacity: 0.75 }}>
          Generate New Code
        </button>
      </div>
    );
  }

  @action.bound
  private _onDurationChange(e: React.ChangeEvent<HTMLSelectElement>) {
    this._durationMinutes = parseInt(e.target.value, 10);
  }

  @action.bound
  private _onBehaviorChange(e: React.ChangeEvent<HTMLInputElement>) {
    this._behavior = e.target.value as 'download' | 'page';
  }

  @action.bound
  private async _generate() {
    this._step = 'generating';
    this._error = '';
    try {
      const result = await apiService.post<{ code: string; expiresAt: string }>(
        `/api/file/${this.props.fileId}/access-code`,
        { durationMinutes: this._durationMinutes, behavior: this._behavior }
      );
      runInAction(() => {
        this._code = result.code;
        this._expiresAt = result.expiresAt;
        this._step = 'generated';
      });
    } catch {
      runInAction(() => {
        this._error = 'Failed to generate code. Please try again.';
        this._step = 'error';
      });
    }
  }

  @action.bound
  private _onGenerateNew() {
    this._step = 'configuring';
    this._code = '';
    this._expiresAt = '';
    this._copySuccess = false;
  }

  @action.bound
  private _onCopy() {
    navigator.clipboard.writeText(this._code)
      .then(() => {
        runInAction(() => { this._copySuccess = true; });
        setTimeout(() => runInAction(() => { this._copySuccess = false; }), 2000);
      })
      .catch(() => {});
  }
}
